import { hasLos } from './lighting';
import type { Seg } from '@/engine/math';

/** Visual overlap only; never changes collision, monster sight, or exploration. */
export interface Rect { x: number; y: number; w: number; h: number }
export function overlaps(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
export function coverOpacity(current: number, covered: boolean, dt: number): number {
  const target = covered ? 0.28 : 1;
  const value = current + (target - current) * (1 - Math.exp(-Math.max(0, Math.min(dt, 0.1)) * (covered ? 16 : 9)));
  return Math.abs(value - target) < 0.002 ? target : value;
}

/** Ignore only the emitting object's own two-sided footprint edges. */
export function withoutEmitter(segs: Seg[], footprint: Rect): Seg[] {
  return segs.filter(s => s.nx !== undefined ||
    !(s.ax > footprint.x && s.ax < footprint.x + footprint.w && s.bx > footprint.x && s.bx < footprint.x + footprint.w &&
      s.ay > footprint.y && s.ay < footprint.y + footprint.h && s.by > footprint.y && s.by < footprint.y + footprint.h));
}

/** A tall drawing may project above a wall, but its physical footprint must be visible.
 * Test the corners too: a wide cabinet should not pop out when its centre is behind a jamb.
 * Use architectural blockers here; furniture still occludes other furniture by depth sorting.
 */
export function visibleFootprint(x: number, y: number, r: Rect, walls: Seg[]): boolean {
  const inset = Math.min(2, r.w / 4, r.h / 4);
  const x0 = r.x + inset, x1 = r.x + r.w - inset;
  const y0 = r.y + inset, y1 = r.y + r.h - inset;
  return hasLos(x, y, (x0 + x1) / 2, (y0 + y1) / 2, walls) ||
    hasLos(x, y, x0, y0, walls) || hasLos(x, y, x1, y0, walls) ||
    hasLos(x, y, x0, y1, walls) || hasLos(x, y, x1, y1, walls);
}

/** Close the same optical boundary as the adjoining wall, including oblique jamb rays. */
export function doorLightSegments(d: { x: number; y: number; w: number; orient: 'h' | 'v'; open: boolean }): Seg[] {
  if (d.open) return [];
  const x0 = d.x * 16, y0 = d.y * 16;
  if (d.orient === 'h') {
    const x1 = (d.x + d.w) * 16;
    return [
      { ax: x0, ay: y0, bx: x1, by: y0, nx: 0, ny: 1 },
      { ax: x0, ay: y0, bx: x1, by: y0, nx: 0, ny: -1 },
    ];
  }
  const x1 = x0 + 16, y1 = (d.y + d.w) * 16;
  return [
    { ax: x0, ay: y0, bx: x0, by: y1, nx: -1, ny: 0 },
    { ax: x1, ay: y0, bx: x1, by: y1, nx: 1, ny: 0 },
  ];
}
